/** Agreement draft: upload Word/PDF — convert to on-screen text (AI) or show the PDF as-is. */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, FileUp } from 'lucide-react';
import api from '../../lib/api';
import { btnGhost, errorText } from '../finance/financeUi';

export function SourceUpload({ draft, onSaved }) {
  const [busy, setBusy] = useState(false);
  const converting = draft?.conversion?.status === 'CONVERTING';
  useEffect(() => {
    if (!converting) return undefined;
    const t = setInterval(onSaved, 4000);
    return () => clearInterval(t);
  }, [converting, onSaved]);
  if (!draft) return null;
  const upload = async (mode, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try { await api.request(`/admin/agreements/versions/${draft.version_id}/upload?mode=${mode}`, { method: 'POST', body: fd }); toast.success(mode === 'pdf' ? 'PDF attached — partners will see it exactly as uploaded' : 'Converting to on-screen text…'); onSaved(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-lg border bg-background p-3" data-testid="source-upload">
      <p className="text-sm font-medium">Upload Word or PDF</p>
      <div className="flex flex-wrap gap-2">
        <label className={`${btnGhost} cursor-pointer ${busy ? 'opacity-50' : ''}`}><FileText className="h-4 w-4" />Convert to on-screen text<input type="file" accept=".docx,.pdf" className="hidden" disabled={busy} onChange={(e) => { upload('convert', e.target.files?.[0]); e.target.value = ''; }} data-testid="source-convert-input" /></label>
        <label className={`${btnGhost} cursor-pointer ${busy ? 'opacity-50' : ''}`}><FileUp className="h-4 w-4" />Show my PDF as-is<input type="file" accept="application/pdf" className="hidden" disabled={busy} onChange={(e) => { upload('pdf', e.target.files?.[0]); e.target.value = ''; }} data-testid="source-pdf-input" /></label>
      </div>
      {converting && <p className="text-xs text-sky-700" data-testid="source-converting">Converting {draft.source_filename}… keep this page open.</p>}
      {draft.conversion?.error && <p className="text-xs text-amber-700" data-testid="source-convert-error">{draft.conversion.error}</p>}
      {draft.display_mode === 'PDF' && draft.file_path && <p className="text-xs text-emerald-700" data-testid="source-pdf-mode">Partners see your PDF as-is, scroll to the end, tick the acknowledgments and sign. The signed copy is your PDF plus a signature page.</p>}
      {draft.display_mode === 'TEXT' && draft.conversion?.status === 'DONE' && <p className="text-xs text-emerald-700" data-testid="source-text-mode">Converted. Compare with the original below, fix anything, then save and publish.</p>}
    </div>
  );
}

export function SourceCompare({ draft }) {
  if (draft?.display_mode !== 'TEXT' || !draft.source_text) return null;
  return (
    <details className="rounded-lg border bg-background p-3 text-sm" open data-testid="source-compare">
      <summary className="cursor-pointer font-medium">Original text ({draft.source_filename}) — side by side with the editor</summary>
      <pre className="mt-2 max-h-[420px] overflow-auto whitespace-pre-wrap font-sans text-xs text-muted-foreground">{draft.source_text}</pre>
    </details>
  );
}
